/**
 * RSA加密工具
 * 用于前端密码加密传输
 * 使用 jsencrypt 库进行RSA加密
 */

let JSEncrypt = null

/**
 * 动态加载JSEncrypt库
 */
async function loadJSEncrypt() {
  if (JSEncrypt) {
    return JSEncrypt
  }
  
  try {
    // 动态导入jsencrypt
    const jsencrypt = await import('jsencrypt')
    // 尝试多种方式获取JSEncrypt类
    JSEncrypt = jsencrypt.default || jsencrypt.JSEncrypt || jsencrypt
    
    // 验证JSEncrypt是否可用
    if (!JSEncrypt || typeof JSEncrypt !== 'function') {
      throw new Error('JSEncrypt类不可用')
    }
    
    return JSEncrypt
  } catch (error) {
    console.error('加载JSEncrypt库失败:', error)
    throw new Error('加密库加载失败，请检查网络连接或刷新页面重试')
  }
}

/**
 * 使用RSA公钥加密数据
 * @param {String} data 待加密的数据
 * @param {String} publicKey Base64编码的公钥（需要转换为PEM格式）
 * @returns {Promise<String>} Base64编码的加密数据
 */
export async function encrypt(data, publicKey) {
  try {
    // 验证输入参数
    if (!data || typeof data !== 'string' || data.trim().length === 0) {
      throw new Error('待加密数据不能为空')
    }
    if (!publicKey || typeof publicKey !== 'string' || publicKey.trim().length === 0) {
      throw new Error('公钥不能为空')
    }
    
    const JSEncryptClass = await loadJSEncrypt()
    if (!JSEncryptClass) {
      throw new Error('加密库加载失败')
    }
    
    // 验证JSEncrypt类是否可用
    if (typeof JSEncryptClass !== 'function') {
      throw new Error('加密库格式错误，JSEncrypt类不可用')
    }
    
    const encrypt = new JSEncryptClass()
    
    // 验证encrypt对象是否创建成功
    if (!encrypt || typeof encrypt.setPublicKey !== 'function') {
      throw new Error('加密对象初始化失败')
    }
    
    // 清理公钥字符串（移除可能的换行符、空格和特殊字符）
    let cleanPublicKey = publicKey.trim()
    
    // 移除PEM格式标记（如果存在），只保留Base64内容
    cleanPublicKey = cleanPublicKey
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/-----BEGIN RSA PUBLIC KEY-----/g, '')
      .replace(/-----END RSA PUBLIC KEY-----/g, '')
      .replace(/\s+/g, '') // 移除所有空白字符
    
    // 验证Base64格式
    if (!cleanPublicKey || cleanPublicKey.length === 0) {
      throw new Error('公钥内容为空')
    }
    
    // 验证是否为有效的Base64字符串
    let base64Valid = false
    try {
      // 尝试解码Base64，验证格式
      if (typeof atob !== 'undefined') {
        atob(cleanPublicKey)
        base64Valid = true
      } else if (typeof Buffer !== 'undefined') {
        Buffer.from(cleanPublicKey, 'base64')
        base64Valid = true
      }
    } catch (e) {
      throw new Error('公钥格式无效：不是有效的Base64字符串')
    }
    
    if (!base64Valid) {
      throw new Error('无法验证Base64格式（浏览器环境不支持）')
    }
    
    // 将Base64字符串按64字符一行分割，转换为PEM格式
    // JSEncrypt需要标准的PEM格式：-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----
    const formattedKey = cleanPublicKey.match(/.{1,64}/g)
    if (!formattedKey || formattedKey.length === 0) {
      throw new Error('公钥格式无效：无法格式化')
    }
    
    // 构建标准PEM格式（PKCS#8格式，X509编码）
    // 后端返回的是X509格式的Base64编码，需要转换为PEM格式
    const pemPublicKey = `-----BEGIN PUBLIC KEY-----\n${formattedKey.join('\n')}\n-----END PUBLIC KEY-----`
    
    // 设置公钥（JSEncrypt的setPublicKey在失败时会抛出异常，而不是返回false）
    let publicKeySet = false
    let lastError = null
    
    // 首先尝试标准格式（PKCS#8，X509编码）- 这是Java后端返回的格式
    try {
      encrypt.setPublicKey(pemPublicKey)
      publicKeySet = true
      console.log('使用标准格式（PKCS#8/X509）设置公钥成功')
    } catch (err1) {
      lastError = err1
      console.warn('标准格式设置失败，尝试RSA格式:', err1.message)
      
      // 如果标准格式失败，尝试RSA格式（PKCS#1格式）
      try {
        const rsaPemKey = `-----BEGIN RSA PUBLIC KEY-----\n${formattedKey.join('\n')}\n-----END RSA PUBLIC KEY-----`
        encrypt.setPublicKey(rsaPemKey)
        publicKeySet = true
        console.log('使用RSA格式（PKCS#1）设置公钥成功')
      } catch (err2) {
        lastError = err2
        console.error('RSA格式设置也失败:', err2.message)
      }
    }
    
    if (!publicKeySet) {
      // 提供详细的调试信息
      console.error('公钥设置失败 - 调试信息:')
      console.error('原始公钥长度:', publicKey.length)
      console.error('清理后公钥长度:', cleanPublicKey.length)
      console.error('格式化后行数:', formattedKey.length)
      console.error('公钥前50字符:', cleanPublicKey.substring(0, 50))
      console.error('PEM格式预览:', pemPublicKey.substring(0, 100) + '...')
      console.error('最后一次错误:', lastError)
      
      // 检查是否是Base64格式问题
      if (lastError && lastError.message) {
        if (lastError.message.includes('Invalid') || lastError.message.includes('invalid')) {
          throw new Error('公钥格式无效：后端返回的公钥可能不是有效的X509格式。请检查后端密钥生成逻辑。')
        }
      }
      
      throw new Error('设置公钥失败：标准格式（PKCS#8）和RSA格式（PKCS#1）均无法设置。错误: ' + (lastError ? lastError.message : '未知错误'))
    }
    
    // 执行加密
    const encrypted = encrypt.encrypt(data)
    if (!encrypted || typeof encrypted !== 'string' || encrypted.trim().length === 0) {
      throw new Error('加密操作返回空结果')
    }
    
    return encrypted
  } catch (error) {
    console.error('RSA加密失败:', error)
    console.error('错误详情:', {
      message: error.message,
      stack: error.stack,
      publicKeyLength: publicKey ? publicKey.length : 0,
      publicKeyPreview: publicKey ? publicKey.substring(0, 50) + '...' : 'null'
    })
    // 如果是已知的错误，直接抛出；否则包装为通用错误
    if (error.message && (
      error.message.includes('不能为空') ||
      error.message.includes('格式无效') ||
      error.message.includes('加载失败') ||
      error.message.includes('设置公钥失败') ||
      error.message.includes('返回空结果') ||
      error.message.includes('公钥内容为空')
    )) {
      throw error
    }
    throw new Error('密码加密失败，请重试: ' + (error.message || '未知错误'))
  }
}

